import React, { useState } from "react";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import "../../../../../sass/components/my-documents-page.scss";
import { Add, Delete, Edit, InfoOutlined } from "@mui/icons-material";
import { usePrimaryOrganizationId } from "../../../hooks";
import { useDpa, useDpaApprovalParties } from "../../../hooks/useDataProtectionAssessment";
import { PageLoading } from "../../../../common/components/Loading";
import { RouteComponentProps, useHistory } from "react-router";
import { Button, Tooltip } from "@mui/material";
import { AnyObject, Form } from "react-final-form";
import { Text } from "../../../../common/components/input/Text";
import { useActionRequest } from "../../../../common/hooks/api";
import { Api } from "../../../../common/service/Api";
import {
  DPAApprovalPartyDto,
  DPAApprovalPartyPurpose,
  DPAApprovalPartyType,
  UpdateDPAApprovalPartyRequest,
} from "../../../../common/service/server/dto/DataProtectionAssessmentDto";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { RouteHelpers, Routes } from "../../../root/routes";
import { DPAProgressStepper } from "./DPAProgressStepper";
import { DPATipsWhoSigns } from "./DPATips";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "../../../components/dialog";
import { requiredEmail, requiredString } from "../../../util/validation";

type MatchProps = {
  dpaId: string;
};
type Props = RouteComponentProps<MatchProps> & {
  add: boolean;
};

export const DPAAddApprovalParties: React.FC<Props> = ({ match }) => {
  const history = useHistory();
  const dpaId = match.params.dpaId;
  const orgId = usePrimaryOrganizationId();
  const [dpa, fetchDpaRequestState] = useDpa(orgId, dpaId);
  const [approvalParties, fetchApprovalPartiesRequestState, refreshApprovalParties] =
    useDpaApprovalParties(orgId, dpaId);

  const { fetch: addPartyRequest } = useActionRequest({
    api: async (values) => {
      await Api.dataProtectionAssessment.addApprovalParty(orgId, dpaId, {
        name: values.name,
        title: values.title,
        email: values.email,
        purpose: values.purpose,
        partyType: values.partyType,
      });
    },
  });

  const { fetch: updatePartyRequest } = useActionRequest({
    api: async (values) => {
      await Api.dataProtectionAssessment.updateDpaApprovalParty(orgId, dpaId, values.partyId, {
        name: values.name,
        title: values.title,
        email: values.email,
      } as UpdateDPAApprovalPartyRequest);
    },
  });

  const { fetch: removeParty } = useActionRequest({
    api: async (id) => {
      await Api.dataProtectionAssessment.removeDpaApprovalParty(orgId, dpaId, id);
    },
  });

  const onSubmit = async (values) => {
    await addPartyRequest(values);
    await refreshApprovalParties();
  };

  const onUpdate = async (values) => {
    await updatePartyRequest(values);
    await refreshApprovalParties();
  };

  const onRemove = async (id) => {
    await removeParty(id);
    await refreshApprovalParties();
  };

  if (!orgId || !dpa || !approvalParties) {
    return <PageLoading />;
  }

  const hasAddedSigner = approvalParties.some((party) => party.purpose === "SIGNER");

  return (
    <StandardPageWrapper
      requests={[fetchDpaRequestState, fetchApprovalPartiesRequestState]}
      footer={
        <ProgressFooter
          actions={
            <ProgressFooterActions
              prevContent={"Back"}
              prevOnClick={() =>
                history.push(RouteHelpers.documents.dataProtectionAssessment.write(dpa.id))
              }
              nextContent={"Next: Get Signatures"}
              nextLoading={false}
              nextOnClick={() =>
                history.push(
                  RouteHelpers.documents.dataProtectionAssessment.gatherSignatures(dpa.id),
                )
              }
              nextDisabled={!hasAddedSigner}
              nextDisabledTooltip="You must add at least one signatory."
            />
          }
        />
      }
    >
      <Link to={Routes.documents.dataProtectionAssessments} className="text-decoration-none">
        <div
          className="text-t1 color-neutral-700"
          style={{ lineHeight: "21px", letterSpacing: "1.12px" }}
        >
          &lt; BACK TO MY ASSESSMENTS
        </div>
      </Link>
      <PageHeader titleContent={dpa.name} />
      <div className="mt-mdlg mb-lg">
        <DPAProgressStepper activeStep={1} />
      </div>
      <div className="text-color-primary text-t5 text-weight-medium">Add Parties</div>
      <div className="text-t3 text-weight-medium mt-xs">
        List all contacts and representatives who have contributed to your assessment.
      </div>

      <div className="mt-lg">
        <ApprovalPartiesTable
          classNames="mt-mdlg"
          editable={true}
          title="Internal Contributors"
          addButtonTitle="Add Internal Contributor"
          tooltipMessage="Internal contributors are individuals who are employed at your business, including executive officers."
          approvalParties={approvalParties.filter(
            (party) => party.partyType === "INTERNAL" && party.purpose === "CONTRIBUTOR",
          )}
          partyType="INTERNAL"
          purpose="CONTRIBUTOR"
          onSubmit={onSubmit}
          onRemove={onRemove}
          onUpdate={onUpdate}
          emptyMessage="No Internal Contributors Added"
        />
        <ApprovalPartiesTable
          classNames="mt-mdlg"
          editable={true}
          title="External Contributors"
          addButtonTitle="Add External Contributor"
          tooltipMessage="External contributors include vendors, independent contractors, and other outside professionals who provided assistance."
          approvalParties={approvalParties.filter(
            (party) => party.partyType === "EXTERNAL" && party.purpose === "CONTRIBUTOR",
          )}
          partyType="EXTERNAL"
          purpose="CONTRIBUTOR"
          onSubmit={onSubmit}
          onRemove={onRemove}
          onUpdate={onUpdate}
          emptyMessage="No External Contributors Added"
        />

        <div className="text-t3 text-weight-medium mt-xs">
          List parties to whom the DPA will be sent.
        </div>
        <DPATipsWhoSigns />
        <ApprovalPartiesTable
          classNames="mt-mdlg"
          editable={true}
          title="DPA Signatories"
          addButtonTitle="Add Signatory"
          approvalParties={approvalParties.filter((party) => party.purpose === "SIGNER")}
          partyType="EXTERNAL"
          purpose="SIGNER"
          onSubmit={onSubmit}
          onRemove={onRemove}
          onUpdate={onUpdate}
          emptyMessage="No DPA Signatories Added"
        />
      </div>
    </StandardPageWrapper>
  );
};

const PartyForm = ({
  onSubmit,
  onCancel,
  submitButtonText,
}: {
  onSubmit: (values: any) => Promise<AnyObject>;
  onCancel: () => void;
  submitButtonText: string;
}) => {
  return (
    <div className="background-color-neutral-000">
      <form onSubmit={onSubmit}>
        <div className="add-parties-row-3">
          <div>
            <Text field="name" label="Name" autoFocus required />
          </div>
          <div>
            <Text field="title" label="Title" required />
          </div>
          <div>
            <Text field="email" label="Email" required />
          </div>
        </div>
        <div className="d-flex justify-end pr-sm pb-sm">
          <Button variant="contained" color="primary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" className="ml-sm">
            {submitButtonText}
          </Button>
        </div>
      </form>
    </div>
  );
};

const addPartyValidation = (values) => {
  const errors = {} as any;
  requiredString(values, "name", errors);
  requiredEmail(values, "email", errors);
  requiredString(values, "title", errors);
  return errors;
};

export const ApprovalPartiesTable = ({
  editable,
  title,
  tooltipMessage,
  addButtonTitle,
  approvalParties,
  partyType,
  purpose,
  onSubmit,
  onRemove,
  onUpdate,
  classNames,
  emptyMessage,
}: {
  editable: boolean;
  title?: string;
  tooltipMessage?: string;
  addButtonTitle: string;
  approvalParties: DPAApprovalPartyDto[];
  partyType: DPAApprovalPartyType;
  purpose: DPAApprovalPartyPurpose;
  onSubmit?: (values: any) => Promise<void>;
  onRemove?: (id: string) => Promise<void>;
  onUpdate?: (values: any) => Promise<void>;
  classNames?: string;
  emptyMessage?: string;
}) => {
  const [isAddingParty, setIsAddingParty] = useState(false);

  const [editingPartyId, setEditingPartyId] = useState<string | null>(null);

  const [confirmRemoveDialogOpen, setConfirmRemoveDialogOpen] = useState(false);

  return (
    <div className={classNames}>
      {title && (
        <div className="d-flex flex--align-center">
          <div className="text-t3 text-weight-medium mr-xs">{title}</div>
          {tooltipMessage && (
            <Tooltip title={tooltipMessage} placement="right">
              <InfoOutlined fontSize={"small"} />
            </Tooltip>
          )}
        </div>
      )}
      <div className="add-parties-table-header-row mt-md">
        <div>Name</div>
        <div>Role</div>
        <div>Email</div>
      </div>
      <div>
        {approvalParties.length == 0 && emptyMessage && !isAddingParty ? (
          <div className="mt-md text-t4 text-weight-medium color-dpa-light-gray background-color-neutral-000 p-mdlg d-flex flex--justify-center flex--align-center">
            {emptyMessage}
          </div>
        ) : (
          approvalParties.map((party) => {
            const isTrueVaultParty = party.email === "help@truevault.com";
            if (party.id === editingPartyId) {
              return (
                <Form
                  key={party.id}
                  onSubmit={async (values) => {
                    await onUpdate(values);
                    setEditingPartyId(null);
                  }}
                  validate={addPartyValidation}
                  initialValues={{
                    partyId: party.id,
                    name: party.name,
                    title: party.title,
                    email: party.email,
                  }}
                  render={({ handleSubmit }) => (
                    <PartyForm
                      submitButtonText="Save"
                      onSubmit={handleSubmit}
                      onCancel={() => setEditingPartyId(null)}
                    />
                  )}
                />
              );
            } else {
              return (
                <div
                  className={editable ? "add-parties-row-4" : "add-parties-row-3"}
                  key={party.id}
                >
                  <div>{party.name}</div>
                  <div>{party.title}</div>
                  <div>{party.email}</div>
                  {editable && onRemove && (
                    <>
                      <div className="d-flex">
                        <div
                          onClick={isTrueVaultParty ? undefined : () => setEditingPartyId(party.id)}
                        >
                          {isTrueVaultParty ? <Edit color="disabled" /> : <Edit />}
                        </div>
                        <div
                          className="ml-sm"
                          onClick={
                            isTrueVaultParty ? undefined : () => setConfirmRemoveDialogOpen(true)
                          }
                        >
                          {isTrueVaultParty ? <Delete color="disabled" /> : <Delete />}
                        </div>
                      </div>
                      <ConfirmDialog
                        open={confirmRemoveDialogOpen}
                        onClose={() => setConfirmRemoveDialogOpen(false)}
                        confirmText="Yes, Remove"
                        confirmTitle="Are you sure you want to remove this user?"
                        contentText={`This will remove ${party.name} from "${title}".`}
                        onConfirm={async () => {
                          await onRemove(party.id);
                          setConfirmRemoveDialogOpen(false);
                        }}
                      />
                    </>
                  )}
                </div>
              );
            }
          })
        )}
      </div>
      {editable && isAddingParty && onSubmit && (
        <Form
          onSubmit={async (values) => {
            await onSubmit(values);
            setIsAddingParty(false);
          }}
          validate={addPartyValidation}
          initialValues={{
            name: "",
            title: "",
            email: "",
            partyType: partyType,
            purpose: purpose,
          }}
          render={({ handleSubmit }) => (
            <PartyForm
              submitButtonText="Add"
              onSubmit={handleSubmit}
              onCancel={() => setIsAddingParty(false)}
            />
          )}
        />
      )}
      {editable && !isAddingParty && (
        <div className="d-flex justify-end mt-md">
          <Button variant="contained" color="primary" onClick={() => setIsAddingParty(true)}>
            <Add /> {addButtonTitle}
          </Button>
        </div>
      )}
    </div>
  );
};
